import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarCheck, Clock3, BadgeCheck, CheckCircle2, Mail, IndianRupee, Search, LogOut, RefreshCw, Download, Loader2, Home, Phone, FileUp } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/site/Logo";
import { api, formatApiError } from "@/lib/api";

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  confirmed: "bg-blue-100 text-blue-700 border-blue-200",
  completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
  cancelled: "bg-red-100 text-red-600 border-red-200",
};

export default function AdminDashboard() {
  const [admin, setAdmin] = useState(null);
  const [tab, setTab] = useState("bookings");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploadingRef, setUploadingRef] = useState(null);
  const [reportFor, setReportFor] = useState(null);
  const [reportFile, setReportFile] = useState(null);
  const [metrics, setMetrics] = useState({ glucose: "", hba1c: "", cholesterol: "" });
  const fileInputs = useRef({});
  const navigate = useNavigate();

  const submitReport = async () => {
    if (!reportFile) { toast.error("Choose a PDF file first"); return; }
    if (!reportFile.name.toLowerCase().endsWith(".pdf")) { toast.error("Only PDF files allowed"); return; }
    setUploadingRef(reportFor);
    try {
      const fd = new FormData();
      fd.append("file", reportFile);
      if (metrics.glucose) fd.append("glucose", metrics.glucose);
      if (metrics.hba1c) fd.append("hba1c", metrics.hba1c);
      if (metrics.cholesterol) fd.append("cholesterol", metrics.cholesterol);
      await api.post(`/admin/bookings/${reportFor}/report`, fd);
      setBookings((prev) => prev.map((b) => (b.ref === reportFor ? { ...b, report: { filename: reportFile.name, uploaded_at: new Date().toISOString() } } : b)));
      toast.success(`Report uploaded for ${reportFor}`);
      setReportFor(null);
      setReportFile(null);
      setMetrics({ glucose: "", hba1c: "", cholesterol: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingRef(null);
    }
  };

  const load = useCallback(async () => {
    try {
      const [s, b, e] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/bookings", { params: { status: statusFilter || undefined, q: q || undefined } }),
        api.get("/admin/enquiries"),
      ]);
      setStats(s.data);
      setBookings(b.data);
      setEnquiries(e.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        try {
          await api.post("/auth/refresh");
          const me = await api.get("/auth/me");
          setAdmin(me.data);
          return load();
        } catch {
          navigate("/admin/login");
          return;
        }
      }
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, q, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get("/auth/me");
        setAdmin(me.data);
        await load();
      } catch {
        try {
          await api.post("/auth/refresh");
          const me = await api.get("/auth/me");
          setAdmin(me.data);
          await load();
        } catch {
          navigate("/admin/login");
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, q]);

  const updateStatus = async (ref, status) => {
    try {
      const { data } = await api.patch(`/admin/bookings/${ref}`, { status });
      setBookings((prev) => prev.map((b) => (b.ref === ref ? data : b)));
      toast.success(`${ref} → ${status}`);
      const s = await api.get("/admin/stats");
      setStats(s.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    navigate("/admin/login");
  };

  const exportCsv = () => {
    const rows = [["Ref", "Name", "Phone", "Items", "Total", "Date", "Time", "Home Collection", "UTR", "Status", "Created"]];
    bookings.forEach((b) => rows.push([
      b.ref, b.patient.name, b.patient.phone,
      b.items.map((i) => i.name).join(" | "), b.total, b.slot_date, b.slot_time,
      b.home_collection ? "Yes" : "No", b.payment?.utr || "", b.status, b.created_at,
    ]));
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `hemozy-bookings-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="w-8 h-8 text-[#D92638] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      <header className="bg-[#0B192C] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo light />
            <span className="hidden sm:inline text-xs font-semibold tracking-widest uppercase text-slate-400 border-l border-white/15 pl-4">Admin Console</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-xs text-slate-400" data-testid="admin-user-email">{admin?.email}</span>
            <Link to="/" className="text-slate-300 hover:text-white transition-colors" data-testid="admin-view-site"><Home className="w-4.5 h-4.5 w-5 h-5" /></Link>
            <button onClick={logout} data-testid="admin-logout-button"
              className="flex items-center gap-2 text-sm text-slate-300 hover:text-white border border-white/15 rounded-full px-4 py-2 transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4" data-testid="admin-stats">
          {[
            { icon: CalendarCheck, label: "Total Bookings", value: stats?.total_bookings ?? 0, color: "text-[#0B192C]" },
            { icon: Clock3, label: "Pending", value: stats?.pending ?? 0, color: "text-amber-600" },
            { icon: BadgeCheck, label: "Confirmed", value: stats?.confirmed ?? 0, color: "text-blue-600" },
            { icon: CheckCircle2, label: "Completed", value: stats?.completed ?? 0, color: "text-emerald-600" },
            { icon: Mail, label: "Enquiries", value: stats?.enquiries ?? 0, color: "text-[#0D9488]" },
            { icon: IndianRupee, label: "Revenue (confirmed+done)", value: `₹${(stats?.revenue ?? 0).toLocaleString("en-IN")}`, color: "text-[#D92638]" },
          ].map(({ icon: Icon, label, value, color }) => (
            <motion.div key={label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-[0_4px_20px_-4px_rgba(15,25,44,0.06)]">
              <Icon className={`w-5 h-5 ${color}`} />
              <div className="mt-2.5 font-mono-med text-xl font-bold text-[#0B192C]">{value}</div>
              <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">{label}</div>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
          <div className="flex gap-2" data-testid="admin-tabs">
            {[["bookings", "Bookings"], ["enquiries", "Enquiries"]].map(([id, label]) => (
              <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
                className={`text-sm font-semibold px-5 py-2.5 rounded-full border transition-all ${tab === id ? "bg-[#0B192C] text-white border-[#0B192C]" : "bg-white text-slate-600 border-slate-200 hover:border-[#0D9488]"}`}>
                {label}
              </button>
            ))}
          </div>
          <button onClick={load} data-testid="admin-refresh-button"
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#0B192C] transition-colors">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {tab === "bookings" && (
          <div className="mt-6" data-testid="admin-bookings-panel">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ref / name / phone" data-testid="admin-search-input"
                  className="pl-10 pr-4 py-2.5 rounded-full border border-slate-200 bg-white text-sm w-64 focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" />
              </div>
              <div className="flex gap-2">
                {["", "pending", "confirmed", "completed", "cancelled"].map((s) => (
                  <button key={s || "all"} onClick={() => setStatusFilter(s)} data-testid={`admin-filter-${s || "all"}`}
                    className={`text-xs font-semibold px-4 py-2 rounded-full border transition-all ${statusFilter === s ? "bg-[#0D9488] text-white border-[#0D9488]" : "bg-white text-slate-600 border-slate-200 hover:border-[#0D9488]"}`}>
                    {s ? s[0].toUpperCase() + s.slice(1) : "All"}
                  </button>
                ))}
              </div>
              <button onClick={exportCsv} data-testid="admin-export-csv"
                className="ml-auto flex items-center gap-2 text-sm font-semibold text-[#0B192C] border border-slate-200 bg-white rounded-full px-4 py-2.5 hover:border-[#0B192C] transition-colors">
                <Download className="w-4 h-4" /> Export CSV
              </button>
            </div>

            <div className="mt-5 bg-white rounded-2xl border border-slate-200/90 overflow-hidden shadow-[0_4px_20px_-4px_rgba(15,25,44,0.06)]">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-dashboard-bookings-table">
                  <thead>
                    <tr className="bg-[#F8FAFC] text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <th className="px-5 py-3.5 font-semibold">Ref</th>
                      <th className="px-5 py-3.5 font-semibold">Patient</th>
                      <th className="px-5 py-3.5 font-semibold">Tests</th>
                      <th className="px-5 py-3.5 font-semibold">Slot</th>
                      <th className="px-5 py-3.5 font-semibold">Amount / UTR</th>
                      <th className="px-5 py-3.5 font-semibold">Status</th>
                      <th className="px-5 py-3.5 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((b) => (
                      <tr key={b.ref} className="border-b border-slate-100 hover:bg-[#F8FAFC]/60 transition-colors" data-testid={`admin-booking-row-${b.ref}`}>
                        <td className="px-5 py-4 font-mono-med font-semibold text-[#0B192C] whitespace-nowrap">{b.ref}</td>
                        <td className="px-5 py-4">
                          <div className="font-medium text-[#0B192C]">{b.patient.name}</div>
                          <div className="text-xs text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" />{b.patient.phone}</div>
                          {b.home_collection && <div className="text-[10px] font-semibold text-[#D92638] mt-0.5">HOME COLLECTION</div>}
                        </td>
                        <td className="px-5 py-4 max-w-[220px]">
                          <div className="text-xs text-slate-600 truncate" title={b.items.map((i) => i.name).join(", ")}>
                            {b.items.map((i) => i.name).join(", ")}
                          </div>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-xs text-slate-600">{b.slot_date}<br />{b.slot_time}</td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          <div className="font-mono-med font-semibold text-[#0B192C]">₹{b.total.toLocaleString("en-IN")}</div>
                          <div className="font-mono-med text-[10px] text-slate-400">{b.payment?.utr || "—"}</div>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`text-[11px] font-bold px-3 py-1 rounded-full border ${STATUS_STYLES[b.status] || ""}`} data-testid={`admin-status-${b.ref}`}>
                            {b.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex gap-1.5 flex-wrap">
                            {b.status !== "confirmed" && (
                              <button onClick={() => updateStatus(b.ref, "confirmed")} data-testid={`admin-confirm-${b.ref}`}
                                className="text-[10px] font-bold px-2.5 py-1.5 rounded-lg bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 transition-colors">CONFIRM</button>
                            )}
                            {b.status !== "completed" && (
                              <button onClick={() => updateStatus(b.ref, "completed")} data-testid={`admin-complete-${b.ref}`}
                                className="text-[10px] font-bold px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100 transition-colors">DONE</button>
                            )}
                            {b.status !== "cancelled" && (
                              <button onClick={() => updateStatus(b.ref, "cancelled")} data-testid={`admin-cancel-${b.ref}`}
                                className="text-[10px] font-bold px-2.5 py-1.5 rounded-lg bg-red-50 text-red-500 border border-red-200 hover:bg-red-100 transition-colors">CANCEL</button>
                            )}
                            <button onClick={() => { setReportFor(b.ref); setReportFile(null); }} disabled={uploadingRef === b.ref} data-testid={`admin-upload-report-${b.ref}`}
                              className={`text-[10px] font-bold px-2.5 py-1.5 rounded-lg border transition-colors flex items-center gap-1 ${b.report ? "bg-[#0D9488]/10 text-[#0D9488] border-[#0D9488]/30 hover:bg-[#0D9488]/20" : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"}`}>
                              {uploadingRef === b.ref ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileUp className="w-3 h-3" />}
                              {b.report ? "PDF ✓" : "REPORT"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {bookings.length === 0 && (
                      <tr><td colSpan={7} className="px-5 py-14 text-center text-slate-400" data-testid="admin-bookings-empty">No bookings yet. They'll appear here as customers book.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "enquiries" && (
          <div className="mt-6 grid md:grid-cols-2 gap-4" data-testid="admin-enquiries-panel">
            {enquiries.map((en, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-[0_4px_20px_-4px_rgba(15,25,44,0.06)]" data-testid={`admin-enquiry-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-[#0B192C]">{en.name}</div>
                  <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${en.type === "b2b" ? "bg-[#0B192C] text-white" : "bg-[#0D9488]/10 text-[#0D9488]"}`}>
                    {en.type === "b2b" ? "HOSPITAL / B2B" : "GENERAL"}
                  </span>
                </div>
                <div className="mt-1 text-xs text-slate-500">{en.phone}{en.email ? ` · ${en.email}` : ""}</div>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{en.message}</p>
                <div className="mt-3 text-[10px] text-slate-400 font-mono-med">{en.created_at?.slice(0, 16).replace("T", " ")}</div>
              </div>
            ))}
            {enquiries.length === 0 && (
              <div className="md:col-span-2 bg-white rounded-2xl border border-slate-200/90 py-14 text-center text-slate-400" data-testid="admin-enquiries-empty">
                No enquiries yet.
              </div>
            )}
          </div>
        )}
      </main>

      {/* Report upload modal */}
      {reportFor && (
        <div className="fixed inset-0 z-[100] bg-[#0B192C]/60 backdrop-blur-sm flex items-center justify-center p-5" onClick={() => setReportFor(null)}>
          <div className="bg-white rounded-3xl w-full max-w-md p-7 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="report-upload-modal">
            <h3 className="font-bold text-lg text-[#0B192C]">Upload report — <span className="font-mono-med">{reportFor}</span></h3>
            <p className="text-xs text-slate-500 mt-1 mb-5">PDF only. Key values are optional - they power the patient's health trend graphs.</p>
            <label className="block border-2 border-dashed border-slate-300 hover:border-[#0D9488] rounded-2xl py-8 text-center cursor-pointer transition-colors" data-testid="report-file-drop">
              <input type="file" accept="application/pdf" className="hidden" data-testid="report-file-input"
                onChange={(e) => setReportFile(e.target.files[0] || null)} />
              <FileUp className="w-7 h-7 mx-auto text-slate-400" />
              <span className="mt-2 block text-sm font-medium text-slate-600">{reportFile ? reportFile.name : "Choose PDF report"}</span>
            </label>
            <div className="mt-4 grid grid-cols-3 gap-3">
              {[["glucose", "Fasting sugar", "mg/dL"], ["hba1c", "HbA1c", "%"], ["cholesterol", "Cholesterol", "mg/dL"]].map(([k, label, unit]) => (
                <div key={k}>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">{label}</label>
                  <input value={metrics[k]} onChange={(e) => setMetrics({ ...metrics, [k]: e.target.value.replace(/[^\d.]/g, "") })} inputMode="decimal" placeholder={unit} data-testid={`metric-${k}-input`}
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" />
                </div>
              ))}
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setReportFor(null)} data-testid="report-modal-cancel"
                className="flex-1 border border-slate-200 text-slate-600 font-semibold text-sm py-3 rounded-full hover:bg-slate-50 transition-colors">Cancel</button>
              <button onClick={submitReport} disabled={uploadingRef === reportFor} data-testid="report-modal-upload"
                className="flex-1 flex items-center justify-center gap-2 bg-[#D92638] hover:bg-[#B81D2D] text-white font-semibold text-sm py-3 rounded-full transition-all active:scale-[0.98] disabled:opacity-60">
                {uploadingRef === reportFor ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
                {uploadingRef === reportFor ? "Uploading..." : "Upload report"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
